import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { PatientAgentContextRevision } from '../../models/patientAgentContext';
import { patientAgentContextService } from '../../services/patientAgentContextService';
import { authorName, revisionWhen, roleLabel } from './contextPresentation';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: { id: number; name: string };
}

// Every saved version of the context, newest first, with who wrote it.
const ContextHistorySheet: React.FC<Props> = ({ open, onOpenChange, patient }) => {
  const [revisions, setRevisions] = useState<PatientAgentContextRevision[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      setError(false);
      try {
        const res = await patientAgentContextService.revisions(patient.id, nextPage);
        setRevisions((prev) => (nextPage === 1 ? res.revisions : [...prev, ...res.revisions]));
        setPage(res.page);
        setTotalPages(res.totalPages);
      } catch (err) {
        console.warn('ContextHistorySheet: failed to load revisions', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [patient.id],
  );

  useEffect(() => {
    if (open) load(1);
  }, [open, load]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader className="border-b border-border">
          <SheetTitle>Historial del contexto clínico</SheetTitle>
          <SheetDescription>{patient.name}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {error && revisions.length === 0 ? (
            <div role="alert" className="text-[13.5px] text-muted-foreground">
              <p className="m-0 mb-2">No se pudo cargar el historial.</p>
              <Button variant="outline" size="sm" onClick={() => load(1)}>
                Reintentar
              </Button>
            </div>
          ) : loading && revisions.length === 0 ? (
            <p className="m-0 text-[13.5px] text-muted-foreground">Cargando historial…</p>
          ) : revisions.length === 0 ? (
            <p className="m-0 text-[13.5px] text-muted-foreground">Todavía no hay versiones guardadas.</p>
          ) : (
            <ol className="m-0 list-none divide-y divide-border p-0">
              {revisions.map((r) => (
                <li key={r.version} className="py-3 first:pt-0">
                  <p className="m-0 text-[13px] font-semibold">
                    Versión {r.version} · {authorName(r.author)}
                    {r.author && <span className="font-normal text-muted-foreground"> ({roleLabel(r.author.role)})</span>}
                  </p>
                  <p className="m-0 text-xs text-muted-foreground">
                    {revisionWhen(r.createdAt)}
                    {r.source === 'agent_proposal' && ` · propuesta por Sissi AI, aceptada por ${authorName(r.author)}`}
                  </p>
                  <div className="mt-1.5 rounded-lg bg-muted px-2.5 py-2 text-[13px] whitespace-pre-wrap">
                    {r.text || <em className="text-muted-foreground">Contexto borrado</em>}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {page < totalPages && revisions.length > 0 && (
            <div className="pt-3 text-center">
              <Button variant="outline" size="sm" onClick={() => load(page + 1)} disabled={loading}>
                {loading ? 'Cargando…' : 'Cargar más'}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ContextHistorySheet;
